import React, { useState, useEffect } from "react";
import {
  Card,
  Button,
  Table,
  Select,
  Input,
  InputNumber,
  Space,
  message,
  Popconfirm,
  Avatar,
  Tag,
} from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { useList } from "@refinedev/core";
import { Actor, Cast } from "@/types";

const { Option } = Select;

interface CastingSelectorProps {
  value?: Cast[];
  onChange?: (casts: Cast[]) => void;
  contentId?: number;
}

export const CastingSelector: React.FC<CastingSelectorProps> = ({
  value = [],
  onChange,
  contentId,
}) => {
  const [casts, setCasts] = useState<Cast[]>(value);
  const [selectedActorId, setSelectedActorId] = useState<number | null>(null);
  const [characterName, setCharacterName] = useState("");
  const [roleType, setRoleType] = useState<"LEAD" | "SUPPORTING" | "CAMEO" | "VOICE">("LEAD");
  const [description, setDescription] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  const { data: actorsData, isLoading: isLoadingActors } = useList<Actor>({
    resource: "actors",
    pagination: { current: 1, pageSize: 100 },
    filters: searchTerm
      ? [{ field: "search", operator: "contains", value: searchTerm }]
      : [],
  });

  const actors = actorsData?.data || [];

  useEffect(() => {
    setCasts(value);
  }, [value]);

  const handleAddCast = () => {
    if (!selectedActorId) {
      message.warning("Veuillez sélectionner un acteur");
      return;
    }

    if (!characterName.trim()) {
      message.warning("Veuillez entrer le nom du personnage");
      return;
    }

    const selectedActor = actors.find((a) => a.id === selectedActorId);
    if (!selectedActor) {
      message.error("Acteur introuvable");
      return;
    }

    // Vérifier si l'acteur n'est pas déjà dans la liste
    if (casts.some((c) => c.actorId === selectedActorId)) {
      message.warning("Cet acteur est déjà dans la distribution");
      return;
    }

    const newCast: Cast = {
      id: Date.now(), // Temporaire, sera remplacé par le backend
      contentId: contentId || 0,
      actorId: selectedActorId,
      actor: selectedActor,
      characterName: characterName.trim(),
      roleType,
      billingOrder: casts.length + 1,
      description: description.trim() || undefined,
    };

    const updatedCasts = [...casts, newCast];
    setCasts(updatedCasts);
    onChange?.(updatedCasts);

    // Réinitialiser le formulaire
    setSelectedActorId(null);
    setCharacterName("");
    setRoleType("LEAD");
    setDescription("");
    setSearchTerm("");

    message.success("Acteur ajouté à la distribution");
  };

  const handleRemoveCast = (castId: number) => {
    const updatedCasts = casts.filter((c) => c.id !== castId);
    // Réorganiser les billingOrder
    const reorderedCasts = updatedCasts.map((c, index) => ({
      ...c,
      billingOrder: index + 1,
    }));
    setCasts(reorderedCasts);
    onChange?.(reorderedCasts);
    message.success("Acteur retiré de la distribution");
  };

  const handleMoveCast = (castId: number, direction: "up" | "down") => {
    const index = casts.findIndex((c) => c.id === castId);
    if (
      (direction === "up" && index === 0) ||
      (direction === "down" && index === casts.length - 1)
    ) {
      return;
    }

    const newCasts = [...casts];
    const targetIndex = direction === "up" ? index - 1 : index + 1;
    [newCasts[index], newCasts[targetIndex]] = [newCasts[targetIndex], newCasts[index]];

    // Réorganiser les billingOrder
    const reorderedCasts = newCasts.map((c, idx) => ({
      ...c,
      billingOrder: idx + 1,
    }));

    setCasts(reorderedCasts);
    onChange?.(reorderedCasts);
  };

  const getRoleTypeColor = (type: string) => {
    switch (type) {
      case "LEAD":
        return "gold";
      case "SUPPORTING":
        return "blue";
      case "CAMEO":
        return "purple";
      case "VOICE":
        return "green";
      default:
        return "default";
    }
  };

  const getRoleTypeLabel = (type: string) => {
    switch (type) {
      case "LEAD":
        return "Principal";
      case "SUPPORTING":
        return "Secondaire";
      case "CAMEO":
        return "Caméo";
      case "VOICE":
        return "Voix";
      default:
        return type;
    }
  };

  const columns = [
    {
      title: "Ordre",
      dataIndex: "billingOrder",
      key: "billingOrder",
      width: 80,
      render: (order: number) => <strong>#{order}</strong>,
    },
    {
      title: "Acteur",
      key: "actor",
      render: (_: any, record: Cast) => (
        <Space>
          {record.actor.profileImageUrl ? (
            <Avatar size={40} src={record.actor.profileImageUrl} />
          ) : (
            <Avatar size={40} icon={<UserOutlined />} />
          )}
          <div>
            <div><strong>{record.actor.fullName}</strong></div>
            <div style={{ fontSize: "12px", color: "#888" }}>
              {record.actor.profession}
            </div>
          </div>
        </Space>
      ),
    },
    {
      title: "Personnage",
      dataIndex: "characterName",
      key: "characterName",
    },
    {
      title: "Type de rôle",
      dataIndex: "roleType",
      key: "roleType",
      render: (type: string) => (
        <Tag color={getRoleTypeColor(type)}>{getRoleTypeLabel(type)}</Tag>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_: any, record: Cast) => (
        <Space>
          <Button
            size="small"
            onClick={() => handleMoveCast(record.id, "up")}
            disabled={record.billingOrder === 1}
          >
            ↑
          </Button>
          <Button
            size="small"
            onClick={() => handleMoveCast(record.id, "down")}
            disabled={record.billingOrder === casts.length}
          >
            ↓
          </Button>
          <Popconfirm
            title="Retirer cet acteur ?"
            description="Cette action retirera l'acteur de la distribution"
            onConfirm={() => handleRemoveCast(record.id)}
            okText="Oui"
            cancelText="Non"
          >
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Card title="Distribution (Casting)" size="small">
      <Space direction="vertical" style={{ width: "100%" }} size="large">
        {/* Formulaire d'ajout */}
        <Card type="inner" title="Ajouter un acteur" size="small">
          <Space direction="vertical" style={{ width: "100%" }}>
            <Select
              showSearch
              value={selectedActorId}
              placeholder="Rechercher et sélectionner un acteur"
              style={{ width: "100%" }}
              onSearch={setSearchTerm}
              onChange={setSelectedActorId}
              loading={isLoadingActors}
              filterOption={false}
              notFoundContent={searchTerm ? "Aucun acteur trouvé" : "Tapez pour rechercher"}
            >
              {actors.map((actor) => (
                <Option key={actor.id} value={actor.id}>
                  <Space>
                    {actor.profileImageUrl ? (
                      <Avatar size="small" src={actor.profileImageUrl} />
                    ) : (
                      <Avatar size="small" icon={<UserOutlined />} />
                    )}
                    <span>{actor.fullName}</span>
                    {actor.profession && (
                      <span style={{ color: "#888", fontSize: "12px" }}>
                        ({actor.profession})
                      </span>
                    )}
                  </Space>
                </Option>
              ))}
            </Select>

            <Input
              placeholder="Nom du personnage"
              value={characterName}
              onChange={(e) => setCharacterName(e.target.value)}
            />

            <Select
              placeholder="Type de rôle"
              value={roleType}
              onChange={setRoleType}
              style={{ width: "100%" }}
            >
              <Option value="LEAD">
                <Tag color="gold">Principal</Tag>
              </Option>
              <Option value="SUPPORTING">
                <Tag color="blue">Secondaire</Tag>
              </Option>
              <Option value="CAMEO">
                <Tag color="purple">Caméo</Tag>
              </Option>
              <Option value="VOICE">
                <Tag color="green">Voix</Tag>
              </Option>
            </Select>

            <Input.TextArea
              placeholder="Description (optionnel)"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
            />

            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddCast}
              block
            >
              Ajouter à la distribution
            </Button>
          </Space>
        </Card>

        {/* Liste des acteurs */}
        {casts.length > 0 ? (
          <Table
            dataSource={casts}
            columns={columns}
            rowKey="id"
            pagination={false}
            size="small"
          />
        ) : (
          <div style={{ textAlign: "center", padding: "20px", color: "#888" }}>
            Aucun acteur dans la distribution. Ajoutez-en un ci-dessus.
          </div>
        )}
      </Space>
    </Card>
  );
};